import axios from 'axios';
import { fetchCashRegisters as fetchSalesCashRegisters } from '@/services/salesService';

const API_URL = '/api/tenant/pos';

/**
 * Metadatos del punto de venta: categorías, impuestos, vendedores (people)
 * y cliente default (SEARCHKEY 001). Los productos se cargan aparte con
 * {@link fetchPosProducts} (paginado / búsqueda server-side).
 */
export const fetchPosCatalog = async () => {
  const response = await axios.get(`${API_URL}/catalog`);
  return response.data;
};

/**
 * Productos visibles del POS con paginación keyset.
 * @param {{ q?: string, categoryId?: string, exact?: boolean, cursor?: string|null, limit?: number }} params
 * @returns {Promise<{ products: Array, nextCursor: string|null, hasMore: boolean }>}
 */
export const fetchPosProducts = async (params = {}) => {
  const query = {};
  if (params.q) query.q = params.q;
  if (params.categoryId && params.categoryId !== 'Todos') {
    query.categoryId = params.categoryId;
  }
  if (params.exact) query.exact = '1';
  if (params.cursor) query.cursor = params.cursor;
  if (params.limit) query.limit = params.limit;

  const response = await axios.get(`${API_URL}/products`, { params: query });
  return response.data;
};

/**
 * Catálogo de cajas (HOST) para el selector del POS.
 * Reutiliza GET /api/tenant/sales/cash-registers.
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export const fetchPosCashRegisters = async () => fetchSalesCashRegisters();

/** Busca clientes visibles por nombre, clave o RFC. */
export const searchPosCustomers = async (query) => {
  const response = await axios.get(`${API_URL}/customers`, {
    params: query ? { q: query } : {},
  });
  return response.data;
};

/** Lista tickets pendientes en SHAREDTICKETS. */
export const listSharedTickets = async () => {
  const response = await axios.get(`${API_URL}/shared-tickets`);
  return response.data;
};

/**
 * Aparca (upsert) un ticket pendiente JSON itpv-quasar-v1.
 * @param {object} payload PendingTicketPayload (+ name opcional)
 */
export const parkSharedTicket = async (payload) => {
  const response = await axios.post(`${API_URL}/shared-tickets`, payload);
  return response.data;
};

/**
 * Recupera y elimina un ticket pendiente Quasar/web.
 * @returns {{ id: string, name: string, ticket: object }}
 */
export const takeSharedTicket = async (id) => {
  const response = await axios.post(`${API_URL}/shared-tickets/${encodeURIComponent(id)}/take`);
  return response.data;
};

/** Elimina un ticket pendiente Quasar/web. */
export const deleteSharedTicket = async (id) => {
  const response = await axios.delete(`${API_URL}/shared-tickets/${encodeURIComponent(id)}`);
  return response.data;
};

/**
 * Guarda la venta en el esquema legacy (receipts/tickets/ticketlines/
 * payments/taxlines/stockdiary) dentro de la sesión abierta del HOST indicado.
 * @param {{ host: string, personId: string, customerId?: string, lines: Array, payments: Array }} payload
 * @returns {{receiptId, ticketNumber, money, host, date}}
 */
export const savePosSale = async (payload) => {
  const response = await axios.post(`${API_URL}/sales`, payload);
  return response.data;
};

/** Permisos POS del operador (PEOPLE → ROLES.PERMISSIONS). */
export const fetchPosPersonPermissions = async (personId) => {
  const response = await axios.get(
    `${API_URL}/people/${encodeURIComponent(personId)}/permissions`,
  );
  return response.data;
};

/**
 * Capacidades de devolución/edición de un ticket.
 * @param {string} receiptId
 * @param {string} [personId]
 */
export const fetchPosTicketCapabilities = async (receiptId, personId) => {
  const response = await axios.get(
    `${API_URL}/tickets/${encodeURIComponent(receiptId)}/capabilities`,
    { params: personId ? { personId } : {} },
  );
  return response.data;
};

/**
 * Crea una devolución parcial/total con control de saldo por línea.
 * @param {{
 *  originReceiptId: string,
 *  personId: string,
 *  host?: string,
 *  idempotencyKey?: string,
 *  lines: Array<{ originLine: number, units: number }>,
 *  subtotal?: number,
 *  taxes?: number,
 *  total?: number,
 *  payments: Array<{ paymentType: string, total: number, paidTotal?: number }>
 * }} payload
 */
export const createPosRefund = async (payload) => {
  const response = await axios.post(`${API_URL}/refunds`, payload);
  return response.data;
};

/**
 * Reemplaza un ticket cerrado (edición destructiva Openbravo).
 * @param {string} receiptId
 * @param {{ personId: string, customerId?: string, host?: string, lines: Array, payments: Array }} payload
 */
export const replacePosTicket = async (receiptId, payload) => {
  const response = await axios.put(
    `${API_URL}/tickets/${encodeURIComponent(receiptId)}`,
    payload,
  );
  return response.data;
};

/**
 * Renderiza el ticket activo sin persistirlo, en formato térmico o carta.
 * @param {object} payload
 * @param {'ticket'|'document'} [outputMode='ticket']
 * @returns {{ html: string, resourceName: string, missingResources: string[] }}
 */
export const renderTicketPreview = async (payload, outputMode = 'ticket') => {
  const response = await axios.post(`${API_URL}/tickets/preview-render`, {
    ...payload,
    outputMode,
  });
  return response.data;
};

/**
 * Renderiza una venta guardada como ticket térmico o documento carta.
 * @param {string} receiptId
 * @param {'ticket'|'document'} [outputMode='ticket']
 * @returns {{ html: string, resourceName: string, missingResources: string[], ticketNumber?: number, receiptId?: string }}
 */
export const renderTicketFinal = async (receiptId, outputMode = 'ticket') => {
  const response = await axios.post(`${API_URL}/tickets/final-render`, {
    receiptId,
    outputMode,
  });
  return response.data;
};

/** Obtiene el comprobante previo tamaño carta como PDF. */
export const fetchTicketPreviewPdf = async (payload) => {
  const response = await axios.post(`${API_URL}/tickets/preview-pdf`, payload, {
    responseType: 'blob',
  });
  return response.data;
};

/** Obtiene el comprobante persistido tamaño carta como PDF. */
export const fetchTicketFinalPdf = async (receiptId) => {
  const response = await axios.post(`${API_URL}/tickets/final-pdf`, { receiptId }, {
    responseType: 'blob',
  });
  return response.data;
};
